import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useApi } from '../hooks/useApi';
import { ecosystemProducts } from '../data/productLine';

const ORDER_BUMPS = [
  { type: 'ecosystem', id: 'canvas', name: 'Canvas', format: '5ml Roller', amount: 2800, requiredTier: null },
  { type: 'ecosystem', id: 'zero', name: 'Zero', format: '50ml Stick', amount: 3600, requiredTier: null },
  { type: 'ecosystem', id: 'silence', name: 'Silence', format: '100ml Spray', amount: 3200, requiredTier: null },
];

export default function CartDrawer() {
  const { t } = useTranslation();
  const { items, total, isOpen, closeCart, removeItem, updateQuantity, invitationCode, setInvitationCode, addItem, canAdd } = useCart();
  const { apiFetch } = useApi();
  const drawerRef = useRef(null);

  // Focus trap + escape key
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key === 'Escape') closeCart();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeCart]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  async function handleCheckout() {
    if (items.length === 0) return;

    try {
      const payload = {
        items: items.map(i => ({ type: i.type, id: i.id, quantity: i.quantity })),
      };
      if (invitationCode) payload.invitation_code = invitationCode;

      const data = await apiFetch('/api/checkout/cart', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (data?.url) {
        // Do not clear cart here — clear only after payment success (Dashboard ?success=true)
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
    }
  }

  // Contextual order bumps — exclude items already in cart, max 2
  const bumpSuggestions = ORDER_BUMPS
    .filter(bump => !items.some(i => i.type === bump.type && i.id === bump.id))
    .filter(bump => canAdd(null, bump.requiredTier))
    .slice(0, 2);

  const hasTierInCart = items.some(i => i.type === 'tiers');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
          />
          <motion.aside
            ref={drawerRef}
            className="cart-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.35, ease: [0.33, 1, 0.68, 1] }}
            role="dialog"
            aria-label="Shopping cart"
          >
            <div className="cart-drawer-header">
              <h2>{t('cart.title')}</h2>
              <button className="cart-drawer-close" onClick={closeCart} aria-label="Close cart">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <line x1="3" y1="3" x2="13" y2="13" />
                  <line x1="13" y1="3" x2="3" y2="13" />
                </svg>
              </button>
            </div>

            {items.length === 0 ? (
              <div className="cart-drawer-empty">
                <p>{t('cart.empty')}</p>
                <p className="cart-drawer-empty-sub">{t('cart.emptySub')}</p>
              </div>
            ) : (
              <>
                <div className="cart-drawer-items">
                  <AnimatePresence initial={false}>
                    {items.map(item => (
                      <motion.div
                        key={`${item.type}-${item.id}`}
                        className="cart-item"
                        layout
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20, height: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="cart-item-info">
                          <span className="cart-item-name">{item.name}</span>
                          {item.format && <span className="cart-item-format">{item.format}</span>}
                        </div>
                        <div className="cart-item-controls">
                          {item.type !== 'tiers' && (
                            <div className="cart-item-qty">
                              <button onClick={() => updateQuantity(item.type, item.id, item.quantity - 1)} aria-label="Decrease">-</button>
                              <span>{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.type, item.id, item.quantity + 1)} aria-label="Increase">+</button>
                            </div>
                          )}
                          <span className="cart-item-price">€{((item.amount * item.quantity) / 100).toFixed(0)}</span>
                          <button className="cart-item-remove" onClick={() => removeItem(item.type, item.id)} aria-label="Remove">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2">
                              <line x1="3" y1="3" x2="11" y2="11" />
                              <line x1="11" y1="3" x2="3" y2="11" />
                            </svg>
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {/* Invitation code for sovereign/apex */}
                {hasTierInCart && items.find(i => i.type === 'tiers')?.id !== 'diagnostic' && (
                  <div className="cart-invite-section">
                    <input
                      type="text"
                      className="cart-invite-input"
                      placeholder={t('cart.invitePlaceholder')}
                      value={invitationCode}
                      onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                    />
                  </div>
                )}

                {/* Order bumps */}
                {bumpSuggestions.length > 0 && (
                  <div className="cart-bumps">
                    <p className="cart-bumps-label">{t('cart.complete')}</p>
                    {bumpSuggestions.map(bump => (
                      <div key={bump.id} className="cart-bump-item">
                        <div className="cart-bump-info">
                          <span className="cart-bump-name">{bump.name}</span>
                          <span className="cart-bump-format">{bump.format}</span>
                        </div>
                        <button
                          className="cart-bump-add"
                          onClick={() => addItem(bump)}
                        >
                          + €{(bump.amount / 100).toFixed(0)}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="cart-drawer-footer">
                  <div className="cart-subtotal">
                    <span>{t('cart.subtotal')}</span>
                    <span>€{(total / 100).toFixed(2)}<span style={{ fontSize: '0.7em', marginLeft: 4, color: 'var(--text-secondary)' }}>{t('cart.vatIncl')}</span></span>
                  </div>
                  <p className="cart-tax-note">{t('cart.taxNote')}</p>
                  <button className="cart-checkout-btn" onClick={handleCheckout}>
                    {t('cart.checkout')}
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
